import { useState, useEffect } from "react";
import { useAtom } from "klyva";
import { authStore, login, logout } from "./stores/auth-store";
// Define the shape of the session data
export type RoleT = "FREE" | "PREMIUM" | "PREMIUM_PLUS" | "ADMIN" | "ADMIN_MANAGER" | "SUPER_ADMIN";
interface SessionData {
  token: string;
  userId: string;
  username: string;
  isAuthenticated: boolean;
  role?: RoleT;
  environment?: string;
}

// Define the return type of the useSession hook
interface UseSession {
  isAuthenticated: boolean;
  session: SessionData | null;
  login: (data: SessionData) => void;
  logout: () => void;
}

const SESSION_KEY = "user_session";

function useSession(): UseSession {
  const [session, setSessionState] = useState<SessionData | null>(null);
  const [authState] = useAtom(authStore);
  const { isAuthenticated } = authState;

  // Check if the user is authenticated
  // Load session data from localStorage when the component mounts
  useEffect(() => {
    const storedSession = localStorage.getItem(SESSION_KEY);
    if (storedSession) {
      if (isAuthenticated) {
        setSessionState({
          ...JSON.parse(storedSession),
          isAuthenticated: true,
        });
      } else {
        setSessionState({
          ...JSON.parse(storedSession),
          isAuthenticated: false,
        });
      }
    }
  }, []);

  // Set session data in both state and localStorage
  const loginHandler = (data: SessionData) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify(data));
    setSessionState(data);
    login();
  };

  // Clear session data from state and localStorage
  const logoutHandler = () => {
    localStorage.removeItem(SESSION_KEY);
    setSessionState(null);
    logout();
  };

  return {
    session,
    isAuthenticated,
    login: (data: SessionData) => loginHandler(data),
    logout: logoutHandler,
  };
}

export default useSession;
